import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { environment } from 'environments/environment';
import { merge, Observable, of, Subject, timer } from 'rxjs';
import {
  delay,
  delayWhen,
  map,
  repeatWhen,
  retryWhen,
  shareReplay,
  startWith,
} from 'rxjs/operators';
import { ListOrganisationPermissionsResponse } from '../contract/authorization/list-organisation-permissions.response';
import { ListRegisterPermissionsResponse } from '../contract/authorization/list-register-permissions.response';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private registerPermissions: Record<number, Observable<RegisterPermissionType[]>> = {};
  private organisationPermissions: Record<number, Observable<OrganisationPermissionType[]>> = {};
  private refreshPrompt$ = new Subject<void>();

  PERMISSIONS_TIMEOUT = 30 * 60 * 1000;

  constructor(private http: HttpClient) {}

  private listRegisterPermissions(id: number) {
    return this.http.get<ListRegisterPermissionsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${id}/permissions`,
    );
  }

  private listOrganisationPermissions(id: number) {
    return this.http.get<ListOrganisationPermissionsResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${id}/permissions`,
    );
  }

  private getPermissionsObservable<T>(source$: Observable<{ permissions: T[] }>): Observable<T[]> {
    // Fetch permissions
    return source$.pipe(
      map(({ permissions }) => permissions),
      // Exponential retry - 1s, 4s, 9s, 16s, 25s, 36s...
      retryWhen(errors => errors.pipe(delayWhen((_, i) => timer(Math.pow(i + 1, 2) * 1000)))),
      repeatWhen(notifications =>
        merge(
          // Every 30 minutes re-fetch permissions
          notifications.pipe(delay(this.PERMISSIONS_TIMEOUT)),
          // Refresh when prompted
          this.refreshPrompt$,
        ),
      ),
      startWith<T[]>([]),
      shareReplay(1),
    );
  }

  refreshPermissions() {
    this.refreshPrompt$.next();
  }

  isRegisterAuthorized(registerId: number, permissionType: RegisterPermissionType) {
    if (!registerId || isNaN(registerId)) {
      return of(false);
    }

    if (!this.registerPermissions[registerId]) {
      this.registerPermissions[registerId] = this.getPermissionsObservable(
        this.listRegisterPermissions(registerId),
      );
    }

    return this.registerPermissions[registerId].pipe(
      map(permissions => permissions.includes(permissionType)),
    );
  }

  isOrganisationAuthorized(organisationId: number, permissionType: OrganisationPermissionType) {
    if (!organisationId || isNaN(organisationId)) {
      return of(false);
    }

    if (!this.organisationPermissions[organisationId]) {
      this.organisationPermissions[organisationId] = this.getPermissionsObservable(
        this.listOrganisationPermissions(organisationId),
      );
    }

    return this.organisationPermissions[organisationId].pipe(
      map(permissions => permissions.includes(permissionType)),
    );
  }
}
